<!-- eslint-disable vue/no-bare-strings-in-template @intlify/vue-i18n/no-raw-text -->
<script setup>
import { computed, onMounted, ref } from 'vue';
import { RouterLink } from 'vue-router';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert, useTracking } from 'dashboard/composables';
import LaisCard from 'dashboard/components-next/LaisCard.vue';
import LaisHeader from 'dashboard/components-next/LaisHeader.vue';
import PartnersAPI from 'dashboard/api/partners';
import SupportTicketPortalAPI from 'dashboard/api/supportTicketPortal';

const account = useAccount();
const { track } = useTracking();

const partnerTypeOptions = [
  { value: 'manutencao', label: 'Manutenção' },
  { value: 'vistoriador', label: 'Vistoriador' },
  { value: 'fotografo', label: 'Fotógrafo' },
  { value: 'chaveiro', label: 'Chaveiro' },
  { value: 'advogado', label: 'Jurídico' },
  { value: 'imobiliaria_parceira', label: 'Imobiliária parceira' },
  { value: 'corretor_externo', label: 'Corretor externo' },
];

const orderCategoryOptions = [
  { value: 'manutencao', label: 'Manutenção' },
  { value: 'vistoria', label: 'Vistoria' },
  { value: 'fotografia', label: 'Fotografia' },
  { value: 'chaveiro', label: 'Chaveiro' },
  { value: 'juridico', label: 'Jurídico' },
  { value: 'co_corretagem', label: 'Co-corretagem' },
  { value: 'outro', label: 'Outro' },
];

const grantScopeOptions = [
  { value: 'work_order_operational', label: 'OS operacional' },
  { value: 'listing_readonly', label: 'Imóvel somente leitura' },
  { value: 'contact_minimal', label: 'Contato mínimo' },
  { value: 'conversation_scoped', label: 'Conversa escopada' },
  { value: 'document_scoped', label: 'Documento escopado' },
];

const legalBasisOptions = [
  { value: 'agreement', label: 'Acordo vigente' },
  { value: 'consent', label: 'Consentimento' },
  { value: 'acceptance', label: 'Aceite' },
  { value: 'contract', label: 'Contrato' },
];

const transitionLabels = {
  quote: 'Cotação',
  approve: 'Aprovar',
  schedule: 'Agendar',
  start: 'Iniciar',
  complete: 'Concluir',
  accept: 'Aceitar',
  dispute: 'Disputar',
  rework: 'Retrabalho',
  cancel: 'Cancelar',
};

const defaultPartnerForm = () => ({
  name: '',
  partner_type: 'manutencao',
  kind: 'operational',
  status: 'active',
  specialty: '',
  territory: '',
  sla_first_response_hours: '',
  sla_resolution_hours: '',
});

const defaultAgreementForm = () => ({
  title: 'Acordo operacional',
  currency: 'BRL',
  price_amount: '',
  data_sharing_scope: 'work_order_operational',
  sla_first_response_hours: '',
  sla_resolution_hours: '',
  privacy_terms: '',
});

const defaultOrderForm = () => ({
  title: '',
  category: 'manutencao',
  kind: 'operational',
  description: '',
  reference_code: '',
  currency: 'BRL',
  due_at: '',
  support_ticket_id: '',
  imovel_listing_id: '',
});

const defaultGrantForm = () => ({
  scope: 'work_order_operational',
  legal_basis: 'agreement',
  expires_at: '',
});

const defaultTransitionForm = () => ({
  amount: '',
  approved_amount: '',
  scheduled_for: '',
  due_at: '',
  note: '',
  quality_score: '',
  rating: '5',
  reason: '',
});

const partnerFilters = ref({ status: '', partner_type: '', kind: '' });
const orderFilters = ref({ status: '', category: '', support_ticket_id: '' });
const partners = ref([]);
const orders = ref([]);
const selectedPartnerId = ref(null);
const selectedOrderId = ref(null);
const selectedOrderDetail = ref(null);
const assistanceReport = ref(null);
const isLoading = ref(false);
const isLoadingOrders = ref(false);
const isSavingPartner = ref(false);
const isSavingAgreement = ref(false);
const isSavingOrder = ref(false);
const isSavingGrant = ref(false);
const isRunningAction = ref(false);
const pageError = ref('');
const partnerForm = ref(defaultPartnerForm());
const agreementForm = ref(defaultAgreementForm());
const orderForm = ref(defaultOrderForm());
const grantForm = ref(defaultGrantForm());
const transitionForm = ref(defaultTransitionForm());
const revokeGrantId = ref('');
const revokeReason = ref('');

const compactObject = payload =>
  Object.entries(payload).reduce((acc, [key, value]) => {
    if (value === undefined || value === null || value === '') return acc;
    acc[key] = value;
    return acc;
  }, {});

const idempotencyKey = prefix =>
  `${prefix}:${Date.now()}:${Math.random().toString(36).slice(2, 8)}`;

const errorMessage = error =>
  error?.response?.data?.errors?.join(', ') ||
  error?.response?.data?.error ||
  error?.response?.data?.message ||
  'Não foi possível concluir a operação.';

const selectedPartner = computed(() =>
  partners.value.find(partner => partner.id === selectedPartnerId.value)
);

const selectedOrder = computed(
  () =>
    selectedOrderDetail.value ||
    orders.value.find(order => order.id === selectedOrderId.value) ||
    null
);

const activeGrants = computed(() =>
  (selectedOrder.value?.access_grants || []).filter(grant => grant.active)
);

const availableTransitions = computed(
  () => selectedOrder.value?.allowed_transitions || []
);

const openOrders = computed(() =>
  orders.value.filter(order => !['accepted', 'canceled'].includes(order.status))
);

const stats = computed(() => [
  {
    label: 'Parceiros ativos',
    value: partners.value.filter(partner => partner.status === 'active').length,
    helper: 'Cadastro account-scoped',
  },
  {
    label: 'OS abertas',
    value: openOrders.value.length,
    helper: 'ServiceWorkOrder',
  },
  {
    label: 'Grants ativos',
    value: activeGrants.value.length,
    helper: 'Escopo mínimo',
  },
  {
    label: 'Assistências',
    value: assistanceReport.value?.metrics?.total_tickets || 0,
    helper: assistanceReport.value?.costs?.restricted
      ? 'Custos restritos'
      : 'Custos liberados',
  },
]);

const partnerLabel = partner =>
  partner
    ? `${partner.name} · ${
        partnerTypeOptions.find(item => item.value === partner.partner_type)
          ?.label || partner.partner_type
      }`
    : 'Sem parceiro selecionado';

const statusLabel = value =>
  value
    ? String(value)
        .replace(/_/g, ' ')
        .replace(/^\w/, char => char.toUpperCase())
    : 'Sem status';

const formatDate = value => {
  if (!value) return 'Sem prazo';
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value));
};

const moneyLabel = (value, currency = 'BRL') => {
  if (value === null || value === undefined || value === '') return '-';
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: currency || 'BRL',
  }).format(Number(value));
};

const loadPartners = async () => {
  const { data } = await PartnersAPI.listOrganizations(partnerFilters.value);
  partners.value = data.data || [];
  if (!selectedPartnerId.value && partners.value.length) {
    selectedPartnerId.value = partners.value[0].id;
  }
};

const loadOrderDetail = async order => {
  if (!order?.id) {
    selectedOrderDetail.value = null;
    return;
  }
  const { data } = await PartnersAPI.showServiceWorkOrder(
    order.id,
    order.partner_organization_id
  );
  selectedOrderDetail.value = data.data;
};

const selectOrder = async order => {
  selectedOrderId.value = order?.id || null;
  await loadOrderDetail(order);
  revokeGrantId.value = activeGrants.value[0]?.id || '';
};

const loadOrders = async () => {
  isLoadingOrders.value = true;
  try {
    const partnerId = selectedPartnerId.value || null;
    const { data } = await PartnersAPI.listServiceWorkOrders(
      orderFilters.value,
      partnerId
    );
    orders.value = data.data || [];
    if (!orders.value.length) {
      selectedOrderId.value = null;
      selectedOrderDetail.value = null;
      return;
    }

    const visibleOrder = orders.value.find(
      order => order.id === selectedOrderId.value
    );
    await selectOrder(visibleOrder || orders.value[0]);
  } finally {
    isLoadingOrders.value = false;
  }
};

const loadAssistanceReport = async () => {
  try {
    const { data } = await SupportTicketPortalAPI.assistanceReport();
    assistanceReport.value = data.data;
  } catch {
    assistanceReport.value = null;
  }
};

const refreshAll = async () => {
  pageError.value = '';
  isLoading.value = true;
  try {
    await loadPartners();
    await loadOrders();
    await loadAssistanceReport();
  } catch (error) {
    pageError.value = errorMessage(error);
    useAlert(pageError.value);
  } finally {
    isLoading.value = false;
  }
};

const selectPartner = async partner => {
  selectedPartnerId.value = partner?.id || null;
  selectedOrderId.value = null;
  selectedOrderDetail.value = null;
  await loadOrders();
};

const savePartner = async () => {
  if (!partnerForm.value.name.trim()) {
    useAlert('Informe o nome do parceiro.');
    return;
  }
  isSavingPartner.value = true;
  try {
    const { data } = await PartnersAPI.createOrganization(
      compactObject(partnerForm.value)
    );
    partnerForm.value = defaultPartnerForm();
    await loadPartners();
    selectedPartnerId.value = data.data?.id;
    useAlert('Parceiro cadastrado.');
    track('partner_operations_partner_created', {
      partner_type: data.data?.partner_type,
    });
  } catch (error) {
    useAlert(errorMessage(error));
  } finally {
    isSavingPartner.value = false;
  }
};

const publishAgreement = async () => {
  if (!selectedPartnerId.value) return;
  isSavingAgreement.value = true;
  try {
    await PartnersAPI.publishAgreement(
      selectedPartnerId.value,
      compactObject(agreementForm.value)
    );
    agreementForm.value = defaultAgreementForm();
    await loadPartners();
    useAlert('Acordo publicado.');
  } catch (error) {
    useAlert(errorMessage(error));
  } finally {
    isSavingAgreement.value = false;
  }
};

const createOrder = async () => {
  if (!selectedPartnerId.value || !orderForm.value.title.trim()) {
    useAlert('Selecione um parceiro e informe o título da OS.');
    return;
  }
  isSavingOrder.value = true;
  try {
    const payload = compactObject({
      ...orderForm.value,
      idempotency_key: idempotencyKey('service-work-order'),
    });
    const { data } = await PartnersAPI.createServiceWorkOrder(
      selectedPartnerId.value,
      payload
    );
    orderForm.value = defaultOrderForm();
    await loadOrders();
    await selectOrder(data.data);
    useAlert('Ordem de serviço criada.');
  } catch (error) {
    useAlert(errorMessage(error));
  } finally {
    isSavingOrder.value = false;
  }
};

const transitionPayload = action => {
  if (action === 'quote') {
    return compactObject({
      amount: transitionForm.value.amount,
      currency: selectedOrder.value?.currency || 'BRL',
      payload: { note: transitionForm.value.note },
    });
  }
  if (action === 'approve') {
    return compactObject({
      approved_amount: transitionForm.value.approved_amount,
      acknowledge_conflict: selectedOrder.value?.conflict_of_interest,
    });
  }
  if (action === 'schedule') {
    if (!transitionForm.value.scheduled_for) {
      useAlert('Informe a data de agendamento.');
      return null;
    }
    return compactObject({
      scheduled_for: transitionForm.value.scheduled_for,
      due_at: transitionForm.value.due_at,
    });
  }
  if (action === 'complete') {
    return compactObject({
      quality_score: transitionForm.value.quality_score,
      payload: { note: transitionForm.value.note },
    });
  }
  if (action === 'accept') {
    return compactObject({
      rating: transitionForm.value.rating,
      quality_score: transitionForm.value.quality_score,
      payload: { note: transitionForm.value.note },
    });
  }
  if (['dispute', 'cancel'].includes(action)) {
    if (!transitionForm.value.reason.trim()) {
      useAlert('Informe o motivo.');
      return null;
    }
    return { reason: transitionForm.value.reason };
  }
  if (action === 'rework')
    return { payload: { note: transitionForm.value.note } };
  return {};
};

const runTransition = async action => {
  if (!selectedOrder.value?.id) return;
  const payload = transitionPayload(action);
  if (payload === null) return;
  isRunningAction.value = true;
  try {
    const { data } = await PartnersAPI.transitionServiceWorkOrder(
      selectedOrder.value.id,
      action,
      payload,
      selectedOrder.value.partner_organization_id
    );
    selectedOrderDetail.value = data.data;
    await loadOrders();
    useAlert('OS atualizada.');
  } catch (error) {
    useAlert(errorMessage(error));
  } finally {
    isRunningAction.value = false;
  }
};

const runOperationalAction = async action => {
  if (!selectedOrder.value?.id) return;
  const payloads = {
    record_evidence: compactObject({
      idempotency_key: idempotencyKey('service-work-order-evidence'),
      actor_side: 'internal',
      quality_score: transitionForm.value.quality_score,
      payload: { note: transitionForm.value.note },
    }),
    invoice: compactObject({
      amount: transitionForm.value.amount,
      payload: { note: transitionForm.value.note },
    }),
    register_payment: compactObject({
      amount: transitionForm.value.amount,
      payload: { note: transitionForm.value.note },
    }),
  };
  isRunningAction.value = true;
  try {
    const { data } = await PartnersAPI.transitionServiceWorkOrder(
      selectedOrder.value.id,
      action,
      payloads[action],
      selectedOrder.value.partner_organization_id
    );
    selectedOrderDetail.value = data.data;
    await loadOrders();
    useAlert('Ledger da OS atualizado.');
  } catch (error) {
    useAlert(errorMessage(error));
  } finally {
    isRunningAction.value = false;
  }
};

const grantAccess = async () => {
  if (!selectedOrder.value?.id) return;
  isSavingGrant.value = true;
  try {
    await PartnersAPI.grantAccess(
      selectedOrder.value.id,
      compactObject({
        ...grantForm.value,
        idempotency_key: idempotencyKey('partner-access-grant'),
      }),
      selectedOrder.value.partner_organization_id
    );
    grantForm.value = defaultGrantForm();
    await loadOrderDetail(selectedOrder.value);
    revokeGrantId.value = activeGrants.value[0]?.id || '';
    useAlert('Grant concedido.');
  } catch (error) {
    useAlert(errorMessage(error));
  } finally {
    isSavingGrant.value = false;
  }
};

const revokeAccess = async () => {
  if (!selectedOrder.value?.id || !revokeGrantId.value) return;
  if (!revokeReason.value.trim()) {
    useAlert('Informe o motivo da revogação.');
    return;
  }
  isSavingGrant.value = true;
  try {
    await PartnersAPI.revokeAccess(
      selectedOrder.value.id,
      { grant_id: revokeGrantId.value, reason: revokeReason.value },
      selectedOrder.value.partner_organization_id
    );
    revokeReason.value = '';
    await loadOrderDetail(selectedOrder.value);
    revokeGrantId.value = activeGrants.value[0]?.id || '';
    useAlert('Grant revogado.');
  } catch (error) {
    useAlert(errorMessage(error));
  } finally {
    isSavingGrant.value = false;
  }
};

onMounted(async () => {
  await refreshAll();
  track('partner_operations_console_viewed');
});
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template @intlify/vue-i18n/no-raw-text -->
  <section class="flex h-full flex-col overflow-hidden bg-[#fcfaf6]">
    <LaisHeader
      title="Parceiros, grants e ordens"
      helper="Operação de prestadores vinculada a imóveis, tickets e manutenção."
    >
      <template #actions>
        <RouterLink
          :to="account.accountScopedRoute('administracao_index')"
          class="inline-flex items-center gap-2 rounded-lais border border-lais-border bg-white px-3 py-2 text-[12px] font-semibold text-lais-text"
        >
          <span class="i-lucide-arrow-left size-4" aria-hidden="true" />
          Administração
        </RouterLink>
        <button
          type="button"
          class="inline-flex items-center gap-2 rounded-lais bg-lais-primary px-3 py-2 text-[12px] font-semibold text-white"
          :disabled="isLoading"
          @click="refreshAll"
        >
          <span class="i-lucide-refresh-cw size-4" aria-hidden="true" />
          Atualizar
        </button>
      </template>
    </LaisHeader>

    <div class="flex-1 overflow-auto px-4 py-4 sm:px-6 lg:px-10 lg:py-6">
      <p
        v-if="pageError"
        class="mb-4 rounded-lais border border-red-200 bg-red-50 px-3 py-2 text-[12px] font-semibold text-red-700"
      >
        {{ pageError }}
      </p>

      <div class="grid gap-3 md:grid-cols-4">
        <LaisCard v-for="item in stats" :key="item.label">
          <p class="text-[12px] font-medium text-lais-text-muted">
            {{ item.label }}
          </p>
          <p class="mt-1 text-[24px] font-semibold text-lais-text">
            {{ item.value }}
          </p>
          <p class="mt-1 text-[12px] text-lais-text-muted">
            {{ item.helper }}
          </p>
        </LaisCard>
      </div>

      <div class="mt-4 grid gap-4 xl:grid-cols-[340px_minmax(0,1fr)_420px]">
        <section class="min-w-0 space-y-4">
          <div class="rounded-lais border border-lais-border bg-white p-4">
            <div class="flex items-center justify-between gap-3">
              <div>
                <h2 class="text-[15px] font-semibold text-lais-text">
                  Parceiros
                </h2>
                <p class="text-[12px] text-lais-text-muted">
                  Documento e canais aparecem redigidos.
                </p>
              </div>
              <span
                class="rounded-full bg-emerald-50 px-2.5 py-1 text-[11px] font-semibold text-emerald-700"
              >
                LGPD
              </span>
            </div>

            <div class="mt-3 grid gap-2 sm:grid-cols-3 xl:grid-cols-1">
              <select
                v-model="partnerFilters.status"
                class="rounded-lais border border-lais-border bg-white px-3 py-2 text-[12px]"
                @change="refreshAll"
              >
                <option value="">Todos status</option>
                <option value="active">Ativos</option>
                <option value="pending">Pendentes</option>
                <option value="suspended">Suspensos</option>
              </select>
              <select
                v-model="partnerFilters.partner_type"
                class="rounded-lais border border-lais-border bg-white px-3 py-2 text-[12px]"
                @change="refreshAll"
              >
                <option value="">Todos tipos</option>
                <option
                  v-for="item in partnerTypeOptions"
                  :key="item.value"
                  :value="item.value"
                >
                  {{ item.label }}
                </option>
              </select>
              <select
                v-model="partnerFilters.kind"
                class="rounded-lais border border-lais-border bg-white px-3 py-2 text-[12px]"
                @change="refreshAll"
              >
                <option value="">Comercial e operacional</option>
                <option value="operational">Operacional</option>
                <option value="commercial">Comercial</option>
              </select>
            </div>
          </div>

          <div class="space-y-2">
            <button
              v-for="partner in partners"
              :key="partner.id"
              type="button"
              class="w-full rounded-lais border bg-white p-3 text-left transition"
              :class="
                selectedPartnerId === partner.id
                  ? 'border-lais-primary shadow-lais-soft'
                  : 'border-lais-border hover:border-lais-primary/50'
              "
              @click="selectPartner(partner)"
            >
              <div class="flex items-start justify-between gap-2">
                <div class="min-w-0">
                  <p class="truncate text-[13px] font-semibold text-lais-text">
                    {{ partner.name }}
                  </p>
                  <p class="mt-1 text-[12px] text-lais-text-muted">
                    {{ statusLabel(partner.partner_type) }} ·
                    {{ statusLabel(partner.kind) }}
                  </p>
                </div>
                <span
                  class="shrink-0 rounded-full bg-lais-bg-muted px-2 py-0.5 text-[11px] font-semibold text-lais-text-muted"
                >
                  {{ statusLabel(partner.status) }}
                </span>
              </div>
              <p class="mt-2 text-[11px] text-lais-text-muted">
                Doc:
                {{
                  partner.document_redacted?.present
                    ? partner.document_redacted.value
                    : 'não informado'
                }}
              </p>
            </button>
            <p
              v-if="!partners.length && !isLoading"
              class="rounded-lais border border-dashed border-lais-border bg-white px-3 py-6 text-center text-[12px] text-lais-text-muted"
            >
              Nenhum parceiro encontrado.
            </p>
          </div>

          <form
            class="rounded-lais border border-lais-border bg-white p-4"
            @submit.prevent="savePartner"
          >
            <h3 class="text-[13px] font-semibold text-lais-text">
              Novo parceiro
            </h3>
            <div class="mt-3 grid gap-2">
              <input
                v-model.trim="partnerForm.name"
                class="rounded-lais border border-lais-border px-3 py-2 text-[12px]"
                placeholder="Nome"
              />
              <div class="grid gap-2 sm:grid-cols-2">
                <select
                  v-model="partnerForm.partner_type"
                  class="rounded-lais border border-lais-border px-3 py-2 text-[12px]"
                >
                  <option
                    v-for="item in partnerTypeOptions"
                    :key="item.value"
                    :value="item.value"
                  >
                    {{ item.label }}
                  </option>
                </select>
                <select
                  v-model="partnerForm.kind"
                  class="rounded-lais border border-lais-border px-3 py-2 text-[12px]"
                >
                  <option value="operational">Operacional</option>
                  <option value="commercial">Comercial</option>
                </select>
              </div>
              <input
                v-model.trim="partnerForm.specialty"
                class="rounded-lais border border-lais-border px-3 py-2 text-[12px]"
                placeholder="Especialidade"
              />
              <button
                type="submit"
                class="inline-flex items-center justify-center gap-2 rounded-lais bg-lais-primary px-3 py-2 text-[12px] font-semibold text-white disabled:opacity-60"
                :disabled="isSavingPartner"
              >
                <span class="i-lucide-plus size-4" aria-hidden="true" />
                Cadastrar parceiro
              </button>
            </div>
          </form>
        </section>

        <section class="min-w-0 space-y-4">
          <div class="rounded-lais border border-lais-border bg-white p-4">
            <div class="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h2 class="text-[15px] font-semibold text-lais-text">
                  Ordens de serviço
                </h2>
                <p class="text-[12px] text-lais-text-muted">
                  {{ partnerLabel(selectedPartner) }}
                </p>
              </div>
              <RouterLink
                :to="
                  account.accountScopedRoute(
                    'support_tickets_portal_index',
                    undefined,
                    { view: 'triage', ticket_type: 'maintenance' }
                  )
                "
                class="inline-flex items-center gap-2 rounded-lais border border-lais-border px-3 py-2 text-[12px] font-semibold text-lais-text"
              >
                <span class="i-lucide-wrench size-4" aria-hidden="true" />
                Manutenção nativa
              </RouterLink>
            </div>

            <div class="mt-3 grid gap-2 sm:grid-cols-3">
              <select
                v-model="orderFilters.status"
                class="rounded-lais border border-lais-border bg-white px-3 py-2 text-[12px]"
                @change="loadOrders"
              >
                <option value="">Todos status</option>
                <option value="draft">Draft</option>
                <option value="quoted">Cotada</option>
                <option value="approved">Aprovada</option>
                <option value="scheduled">Agendada</option>
                <option value="in_progress">Em execução</option>
                <option value="completed">Concluída</option>
                <option value="accepted">Aceita</option>
                <option value="canceled">Cancelada</option>
              </select>
              <select
                v-model="orderFilters.category"
                class="rounded-lais border border-lais-border bg-white px-3 py-2 text-[12px]"
                @change="loadOrders"
              >
                <option value="">Todas categorias</option>
                <option
                  v-for="item in orderCategoryOptions"
                  :key="item.value"
                  :value="item.value"
                >
                  {{ item.label }}
                </option>
              </select>
              <input
                v-model.trim="orderFilters.support_ticket_id"
                class="rounded-lais border border-lais-border px-3 py-2 text-[12px]"
                placeholder="Ticket ID"
                @keyup.enter="loadOrders"
              />
            </div>
          </div>

          <div class="grid gap-2 lg:grid-cols-2">
            <button
              v-for="order in orders"
              :key="order.id"
              type="button"
              class="rounded-lais border bg-white p-3 text-left transition"
              :class="
                selectedOrderId === order.id
                  ? 'border-lais-primary shadow-lais-soft'
                  : 'border-lais-border hover:border-lais-primary/50'
              "
              @click="selectOrder(order)"
            >
              <div class="flex items-start justify-between gap-2">
                <div class="min-w-0">
                  <p class="truncate text-[13px] font-semibold text-lais-text">
                    #{{ order.id }} · {{ order.title }}
                  </p>
                  <p class="mt-1 text-[12px] text-lais-text-muted">
                    {{ statusLabel(order.category) }} ·
                    {{ formatDate(order.due_at) }}
                  </p>
                </div>
                <span
                  class="shrink-0 rounded-full bg-lais-primary/10 px-2 py-0.5 text-[11px] font-semibold text-lais-primary"
                >
                  {{ statusLabel(order.status) }}
                </span>
              </div>
              <div
                class="mt-2 flex flex-wrap gap-2 text-[11px] text-lais-text-muted"
              >
                <span v-if="order.support_ticket_id">
                  Ticket {{ order.support_ticket_id }}
                </span>
                <span v-if="order.imovel_listing_id">
                  Imóvel {{ order.imovel_listing_id }}
                </span>
                <span v-if="order.overdue">SLA vencido</span>
              </div>
            </button>
          </div>
          <p
            v-if="!orders.length && !isLoadingOrders"
            class="rounded-lais border border-dashed border-lais-border bg-white px-3 py-6 text-center text-[12px] text-lais-text-muted"
          >
            Nenhuma ordem de serviço encontrada.
          </p>

          <div class="grid gap-4 lg:grid-cols-2">
            <form
              class="rounded-lais border border-lais-border bg-white p-4"
              @submit.prevent="createOrder"
            >
              <h3 class="text-[13px] font-semibold text-lais-text">Nova OS</h3>
              <div class="mt-3 grid gap-2">
                <input
                  v-model.trim="orderForm.title"
                  class="rounded-lais border border-lais-border px-3 py-2 text-[12px]"
                  placeholder="Título"
                />
                <div class="grid gap-2 sm:grid-cols-2">
                  <select
                    v-model="orderForm.category"
                    class="rounded-lais border border-lais-border px-3 py-2 text-[12px]"
                  >
                    <option
                      v-for="item in orderCategoryOptions"
                      :key="item.value"
                      :value="item.value"
                    >
                      {{ item.label }}
                    </option>
                  </select>
                  <input
                    v-model.trim="orderForm.support_ticket_id"
                    class="rounded-lais border border-lais-border px-3 py-2 text-[12px]"
                    placeholder="Ticket ID"
                  />
                </div>
                <div class="grid gap-2 sm:grid-cols-2">
                  <input
                    v-model.trim="orderForm.imovel_listing_id"
                    class="rounded-lais border border-lais-border px-3 py-2 text-[12px]"
                    placeholder="Imóvel listing ID"
                  />
                  <input
                    v-model="orderForm.due_at"
                    type="datetime-local"
                    class="rounded-lais border border-lais-border px-3 py-2 text-[12px]"
                  />
                </div>
                <textarea
                  v-model.trim="orderForm.description"
                  class="min-h-20 rounded-lais border border-lais-border px-3 py-2 text-[12px]"
                  placeholder="Descrição operacional"
                />
                <button
                  type="submit"
                  class="inline-flex items-center justify-center gap-2 rounded-lais bg-lais-primary px-3 py-2 text-[12px] font-semibold text-white disabled:opacity-60"
                  :disabled="isSavingOrder || !selectedPartnerId"
                >
                  <span
                    class="i-lucide-clipboard-plus size-4"
                    aria-hidden="true"
                  />
                  Criar OS
                </button>
              </div>
            </form>

            <form
              class="rounded-lais border border-lais-border bg-white p-4"
              @submit.prevent="publishAgreement"
            >
              <h3 class="text-[13px] font-semibold text-lais-text">
                Acordo vigente
              </h3>
              <div class="mt-3 grid gap-2">
                <input
                  v-model.trim="agreementForm.title"
                  class="rounded-lais border border-lais-border px-3 py-2 text-[12px]"
                  placeholder="Título do acordo"
                />
                <div class="grid gap-2 sm:grid-cols-2">
                  <input
                    v-model.trim="agreementForm.price_amount"
                    class="rounded-lais border border-lais-border px-3 py-2 text-[12px]"
                    placeholder="Valor"
                  />
                  <input
                    v-model.trim="agreementForm.currency"
                    class="rounded-lais border border-lais-border px-3 py-2 text-[12px]"
                    placeholder="BRL"
                  />
                </div>
                <div class="grid gap-2 sm:grid-cols-2">
                  <input
                    v-model.trim="agreementForm.sla_first_response_hours"
                    class="rounded-lais border border-lais-border px-3 py-2 text-[12px]"
                    placeholder="SLA resposta h"
                  />
                  <input
                    v-model.trim="agreementForm.sla_resolution_hours"
                    class="rounded-lais border border-lais-border px-3 py-2 text-[12px]"
                    placeholder="SLA resolução h"
                  />
                </div>
                <textarea
                  v-model.trim="agreementForm.privacy_terms"
                  class="min-h-20 rounded-lais border border-lais-border px-3 py-2 text-[12px]"
                  placeholder="Termos de privacidade"
                />
                <button
                  type="submit"
                  class="inline-flex items-center justify-center gap-2 rounded-lais border border-lais-primary px-3 py-2 text-[12px] font-semibold text-lais-primary disabled:opacity-60"
                  :disabled="isSavingAgreement || !selectedPartnerId"
                >
                  <span
                    class="i-lucide-file-check-2 size-4"
                    aria-hidden="true"
                  />
                  Publicar acordo
                </button>
              </div>
            </form>
          </div>
        </section>

        <aside class="min-w-0 space-y-4">
          <section class="rounded-lais border border-lais-border bg-white p-4">
            <div class="flex items-start justify-between gap-3">
              <div>
                <h2 class="text-[15px] font-semibold text-lais-text">
                  Detalhe da OS
                </h2>
                <p class="text-[12px] text-lais-text-muted">
                  {{
                    selectedOrder
                      ? `#${selectedOrder.id} · ${statusLabel(
                          selectedOrder.status
                        )}`
                      : 'Selecione uma ordem'
                  }}
                </p>
              </div>
              <span
                v-if="selectedOrder?.payload_redaction"
                class="rounded-full bg-emerald-50 px-2.5 py-1 text-[11px] font-semibold text-emerald-700"
              >
                Payload redigido
              </span>
            </div>

            <div v-if="selectedOrder" class="mt-4 space-y-3">
              <div class="grid grid-cols-2 gap-2 text-[12px]">
                <p class="rounded-lais bg-lais-bg-muted p-2">
                  <span class="block text-lais-text-muted">Cotado</span>
                  {{
                    moneyLabel(
                      selectedOrder.quoted_amount,
                      selectedOrder.currency
                    )
                  }}
                </p>
                <p class="rounded-lais bg-lais-bg-muted p-2">
                  <span class="block text-lais-text-muted">Aprovado</span>
                  {{
                    moneyLabel(
                      selectedOrder.approved_amount,
                      selectedOrder.currency
                    )
                  }}
                </p>
                <p class="rounded-lais bg-lais-bg-muted p-2">
                  <span class="block text-lais-text-muted">Faturado</span>
                  {{
                    moneyLabel(
                      selectedOrder.invoiced_amount,
                      selectedOrder.currency
                    )
                  }}
                </p>
                <p class="rounded-lais bg-lais-bg-muted p-2">
                  <span class="block text-lais-text-muted">Pago</span>
                  {{
                    moneyLabel(
                      selectedOrder.paid_amount,
                      selectedOrder.currency
                    )
                  }}
                </p>
              </div>

              <div
                class="rounded-lais border border-lais-border p-3 text-[12px]"
              >
                <p class="font-semibold text-lais-text">Vínculos</p>
                <div class="mt-2 grid gap-1 text-lais-text-muted">
                  <span>
                    Parceiro: {{ selectedOrder.partner_organization_id }}
                  </span>
                  <span>
                    Ticket: {{ selectedOrder.support_ticket_id || '-' }}
                  </span>
                  <span>
                    Imóvel: {{ selectedOrder.imovel_listing_id || '-' }}
                  </span>
                  <span>
                    Lead referral: {{ selectedOrder.lead_referral_id || '-' }}
                  </span>
                </div>
              </div>

              <div class="grid gap-2">
                <div class="grid gap-2 sm:grid-cols-2">
                  <input
                    v-model.trim="transitionForm.amount"
                    class="rounded-lais border border-lais-border px-3 py-2 text-[12px]"
                    placeholder="Valor"
                  />
                  <input
                    v-model.trim="transitionForm.quality_score"
                    class="rounded-lais border border-lais-border px-3 py-2 text-[12px]"
                    placeholder="Qualidade 0-100"
                  />
                </div>
                <div class="grid gap-2 sm:grid-cols-2">
                  <input
                    v-model="transitionForm.scheduled_for"
                    type="datetime-local"
                    class="rounded-lais border border-lais-border px-3 py-2 text-[12px]"
                  />
                  <input
                    v-model="transitionForm.due_at"
                    type="datetime-local"
                    class="rounded-lais border border-lais-border px-3 py-2 text-[12px]"
                  />
                </div>
                <div class="grid gap-2 sm:grid-cols-2">
                  <input
                    v-model.trim="transitionForm.rating"
                    class="rounded-lais border border-lais-border px-3 py-2 text-[12px]"
                    placeholder="Nota 1-5"
                  />
                  <input
                    v-model.trim="transitionForm.reason"
                    class="rounded-lais border border-lais-border px-3 py-2 text-[12px]"
                    placeholder="Motivo"
                  />
                </div>
                <textarea
                  v-model.trim="transitionForm.note"
                  class="min-h-20 rounded-lais border border-lais-border px-3 py-2 text-[12px]"
                  placeholder="Nota/evidência redigida"
                />
              </div>

              <div class="flex flex-wrap gap-2">
                <button
                  v-for="action in availableTransitions"
                  :key="action"
                  type="button"
                  class="inline-flex items-center gap-1 rounded-lais bg-lais-primary px-3 py-2 text-[12px] font-semibold text-white disabled:opacity-60"
                  :disabled="isRunningAction"
                  @click="runTransition(action)"
                >
                  <span class="i-lucide-play size-4" aria-hidden="true" />
                  {{ transitionLabels[action] || statusLabel(action) }}
                </button>
                <button
                  type="button"
                  class="inline-flex items-center gap-1 rounded-lais border border-lais-border px-3 py-2 text-[12px] font-semibold text-lais-text disabled:opacity-60"
                  :disabled="isRunningAction"
                  @click="runOperationalAction('record_evidence')"
                >
                  <span class="i-lucide-paperclip size-4" aria-hidden="true" />
                  Evidência
                </button>
                <button
                  type="button"
                  class="inline-flex items-center gap-1 rounded-lais border border-lais-border px-3 py-2 text-[12px] font-semibold text-lais-text disabled:opacity-60"
                  :disabled="isRunningAction"
                  @click="runOperationalAction('invoice')"
                >
                  <span class="i-lucide-receipt size-4" aria-hidden="true" />
                  Faturar
                </button>
                <button
                  type="button"
                  class="inline-flex items-center gap-1 rounded-lais border border-lais-border px-3 py-2 text-[12px] font-semibold text-lais-text disabled:opacity-60"
                  :disabled="isRunningAction"
                  @click="runOperationalAction('register_payment')"
                >
                  <span
                    class="i-lucide-badge-check size-4"
                    aria-hidden="true"
                  />
                  Pagamento
                </button>
              </div>
            </div>
          </section>

          <section class="rounded-lais border border-lais-border bg-white p-4">
            <h2 class="text-[15px] font-semibold text-lais-text">
              Grants e permissões
            </h2>
            <p class="text-[12px] text-lais-text-muted">
              Escopo, base legal, expiração e motivo de revogação.
            </p>

            <div class="mt-3 space-y-2">
              <div
                v-for="grant in selectedOrder?.access_grants || []"
                :key="grant.id"
                class="rounded-lais border border-lais-border p-3 text-[12px]"
              >
                <div class="flex items-start justify-between gap-2">
                  <div>
                    <p class="font-semibold text-lais-text">
                      #{{ grant.id }} · {{ statusLabel(grant.scope) }}
                    </p>
                    <p class="mt-1 text-lais-text-muted">
                      {{ statusLabel(grant.legal_basis) }} · expira
                      {{ formatDate(grant.expires_at) }}
                    </p>
                  </div>
                  <span
                    class="rounded-full px-2 py-0.5 text-[11px] font-semibold"
                    :class="
                      grant.active
                        ? 'bg-emerald-50 text-emerald-700'
                        : 'bg-lais-bg-muted text-lais-text-muted'
                    "
                  >
                    {{ statusLabel(grant.status) }}
                  </span>
                </div>
                <p v-if="grant.revoke_reason" class="mt-2 text-lais-text-muted">
                  Motivo: {{ grant.revoke_reason }}
                </p>
              </div>
            </div>

            <div class="mt-4 grid gap-2">
              <div class="grid gap-2 sm:grid-cols-2">
                <select
                  v-model="grantForm.scope"
                  class="rounded-lais border border-lais-border px-3 py-2 text-[12px]"
                >
                  <option
                    v-for="item in grantScopeOptions"
                    :key="item.value"
                    :value="item.value"
                  >
                    {{ item.label }}
                  </option>
                </select>
                <select
                  v-model="grantForm.legal_basis"
                  class="rounded-lais border border-lais-border px-3 py-2 text-[12px]"
                >
                  <option
                    v-for="item in legalBasisOptions"
                    :key="item.value"
                    :value="item.value"
                  >
                    {{ item.label }}
                  </option>
                </select>
              </div>
              <input
                v-model="grantForm.expires_at"
                type="datetime-local"
                class="rounded-lais border border-lais-border px-3 py-2 text-[12px]"
              />
              <button
                type="button"
                class="inline-flex items-center justify-center gap-2 rounded-lais bg-lais-primary px-3 py-2 text-[12px] font-semibold text-white disabled:opacity-60"
                :disabled="isSavingGrant || !selectedOrder"
                @click="grantAccess"
              >
                <span class="i-lucide-key-round size-4" aria-hidden="true" />
                Conceder grant
              </button>
            </div>

            <div class="mt-4 grid gap-2">
              <select
                v-model="revokeGrantId"
                class="rounded-lais border border-lais-border px-3 py-2 text-[12px]"
              >
                <option value="">Grant ativo</option>
                <option
                  v-for="grant in activeGrants"
                  :key="grant.id"
                  :value="grant.id"
                >
                  #{{ grant.id }} · {{ statusLabel(grant.scope) }}
                </option>
              </select>
              <input
                v-model.trim="revokeReason"
                class="rounded-lais border border-lais-border px-3 py-2 text-[12px]"
                placeholder="Motivo da revogação"
              />
              <button
                type="button"
                class="inline-flex items-center justify-center gap-2 rounded-lais border border-red-200 px-3 py-2 text-[12px] font-semibold text-red-700 disabled:opacity-60"
                :disabled="isSavingGrant || !revokeGrantId"
                @click="revokeAccess"
              >
                <span class="i-lucide-key-round-x size-4" aria-hidden="true" />
                Revogar grant
              </button>
            </div>
          </section>
        </aside>
      </div>
    </div>
  </section>
</template>
